import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import Errored from '../Errored';
import Loading from '../Loading';
import OptionsSelector from '../OptionsSelector';
import Paging from '../Paging';
import { globalStyles } from '../style/gstheme';
import { getGraphFromProject, makeCancelable, reverseSortByProp } from '../utils/utils';
import IntegrationLink from './IntegrationLink';

const styles = theme => {
    return Object.assign({
        actionResource: {
            display: 'block'
        }
    }, globalStyles);
};

const PAGE_SIZE = 20;

const viewOptions = [
    {
        value: "date",
        label: "Date"
    },
    {
        value: "project",
        label: "Project"
    },
    {
        value: "resources",
        label: "Resources"
    },
    {
        value: "completed",
        label: "Completed"
    }
];

class IntegrationActionViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            history: null,
            pageCount: 1,
            columns: [true, true, true, true],
            viewType: "option-0",
        };
        this.cancellable = null;
    }

    loadActions = (page = 1) => {
        this.doSearch({
            search: [
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: [
                        "https://grafsync.graphifi.com/schema/gs/IntegrationAction"
                    ]
                },
                {
                    predicate: "https://grafsync.graphifi.com/schema/gs/integration",
                    value: [
                        this.props.integrationIRI
                    ]
                }
            ],
            graph: [GS.getActiveSubscription()],
            page,
            basicSort: true,
            sortOrder: "Descending",
            pageSize: PAGE_SIZE,
            sort: ["https://grafsync.graphifi.com/schema/gs/actionTime"]
        })
            .promise
            .then(data => {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const actions = data.results || [];
                const sortedActions = reverseSortByProp(actions, "actionTime");
                const pageCount = data.pageCount;
                const projectList = actions.map(item => item.project);
                const projects = [...new Set(projectList)];
                const projectsToLoad = projects.map(pr => (
                    {
                        graph: getGraphFromProject(pr),
                        iri: pr
                    }
                ));
                GS.loadDataFromGraph(projectsToLoad).then(() => {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.setState({ actions: sortedActions, page, pageCount });
                });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    doSearch(payload) {
        this.cancellable = makeCancelable(GS.search(payload));
        return this.cancellable;
    }

    componentDidMount() {
        this.loadActions();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    setColumns = (columns) => this.setState({ columns });

    render() {

        const { classes } = this.props;
        const { page, pageCount, isErrored, columns, actions } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        return (
            <div>
                <Typography className={classes.mainHeading}>History of Actions</Typography>
                <div style={{ padding: '12px', backgroundColor: 'white' }}>
                    {actions === null ?
                        <div style={{ marginTop: 120, height: 140 }}>
                            <Loading />
                        </div>
                        :
                        actions && actions.length > 0 ?
                            <div>
                                <div style={{ float: 'right', marginTop: 12, marginRight: 12 }}>
                                    <OptionsSelector
                                        options={viewOptions}
                                        visibleValues={[true, true, true, true]}
                                        values={columns}
                                        onSelect={this.setColumns} />
                                </div>
                                <Table data-test="history-viewer" style={{ maxWidth: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            {
                                                ["Date", "Project", "Resources", "Completed"].map((col, index) =>
                                                    columns[index] &&
                                                    <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                                                )
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {actions.map((item, rowIndex) => {
                                            if (rowIndex === PAGE_SIZE) return null;
                                            const date = item.actionTime ? new Date(item.actionTime) : null;
                                            const projectGraph = getGraphFromProject(item.project);
                                            const projectData = GS.data(item.project, projectGraph, false);
                                            const dateText = date ? date.toLocaleString() : "";
                                            const row = [
                                                dateText,
                                                <IntegrationLink
                                                    key={rowIndex}
                                                    id={item.project}
                                                    projectIRI={item.project}
                                                    handleNotAvailable={true}
                                                    lang={projectData ? projectData.defaultLang : "en"}
                                                    graph={projectGraph} />,
                                                item.actionResource ?
                                                    [...item.actionResource].sort().map((ar, index) => {
                                                        return <span data-test={"action-resource-" + index} key={ar} className={classes.actionResource}>
                                                            <IntegrationLink
                                                                id={ar}
                                                                projectIRI={item.project}
                                                                handleNotAvailable={true}
                                                                lang={projectData ? projectData.defaultLang : "en"}
                                                                graph={projectGraph} />
                                                        </span>;
                                                    })
                                                    :
                                                    null,
                                                item.status && item.status[0] === "https://grafsync.graphifi.com/schema/gs/uploaded" ?
                                                    "Yes"
                                                    :
                                                    "No"
                                            ];
                                            return (
                                                <TableRow key={"row-" + rowIndex} classes={{
                                                    root: classes.stripedRow
                                                }}>
                                                    {row.map((cell, cellIndex) => {
                                                        if (!columns[cellIndex]) return null;
                                                        const spans = cell && typeof cell === "string" ? cell.split("\n") : [cell];
                                                        return <TableCell key={cellIndex} className={classes.tableCell} style={
                                                            cellIndex > 1 && cellIndex < 4 ?
                                                                {
                                                                    minWidth: '100px'
                                                                }
                                                                :
                                                                cellIndex === 4 ?
                                                                    {
                                                                        minWidth: '200px'
                                                                    }
                                                                    :
                                                                    {}}>
                                                            {
                                                                spans.map((spanText, spanIndex) => <div className={classes.tableCellLine} key={spanIndex}>
                                                                    {spanText}
                                                                    </div>
                                                                )
                                                            }
                                                        </TableCell>;
                                                    }
                                                    )}
                                                </TableRow>
                                            );
                                        }
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            :
                            <div data-test="integration-viewer" style={{ marginBottom: 120 }}>
                                <ActionText offset="120px">No integration actions have happened.</ActionText>
                            </div>
                    }
                    {pageCount > 1 &&
                        <div style={{ marginTop: '20px' }}>
                            <Paging page={page} pageCount={pageCount} pageItemsRange={2} handlePageChange={this.loadActions} />
                        </div>
                    }
                </div>
            </div>
        );
    }
}

IntegrationActionViewer.defaultProps = {
};

IntegrationActionViewer.propTypes = {
    integrationIRI: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(IntegrationActionViewer);